import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trophy, Zap } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { estApi } from "../lib/api";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

/**
 * Win → project. The AM bids ten scopes and wins five, so the Won moment asks which.
 *
 * Everything is ticked by default — the common case stays one button. The scopes left unticked
 * are not deleted: they are excluded on the project's working estimate, so what was bid survives.
 * The schedule-acceleration option, when it was offered, is one more tick on the same list: ticked
 * here it is part of the main contract; accepted later it goes in as a change order instead.
 */
export default function WinJobDialog({ open, onOpenChange, estimateId, onWon }) {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState([]);
  const [accel, setAccel] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !estimateId) return;
    setData(null);
    estApi.winPreview(estimateId).then((d) => {
      setData(d);
      setPicked(d.sections.map((s) => s.name));
      setAccel(false);
    }).catch((e) => toast.error(e?.response?.data?.detail || "Could not read the estimate"));
  }, [open, estimateId]);

  const toggle = (name) => setPicked((p) =>
    p.includes(name) ? p.filter((x) => x !== name) : [...p, name]);

  const wonSell = (data?.sections || []).filter((s) => picked.includes(s.name))
    .reduce((t, s) => t + s.sell, 0);
  const contract = wonSell + (accel && data?.acceleration?.offered ? data.acceleration.amount : 0);
  const allPicked = data && picked.length === data.sections.length;

  const push = async () => {
    setBusy(true);
    try {
      const res = await estApi.win(estimateId, {
        sections: allPicked ? null : picked,
        accept_acceleration: accel,
      });
      toast.success("Pushed to the project", {
        description: `Contract ${money(res.operations?.contract_value)}${
          res.operations?.not_won_scopes?.length
            ? ` · ${res.operations.not_won_scopes.length} scope(s) not awarded` : ""}`,
      });
      onOpenChange(false);
      onWon?.(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not push this to a project");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="win-job-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-emerald-600" />
            Which scopes did we win?
          </DialogTitle>
        </DialogHeader>

        {!data ? (
          <div className="flex items-center gap-2 py-10 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Reading the estimate…
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              {data.estimate_number} · {data.project_name}. Everything is ticked — untick anything
              the GC did not award. Unticked scopes are kept on the working estimate as excluded,
              so the bid history survives and one can come back later.
            </p>

            <div className="max-h-72 overflow-auto rounded-lg border border-slate-200"
                 data-testid="win-sections">
              {data.sections.length === 0 && (
                <p className="p-4 text-sm text-slate-500">This estimate has no priced scopes.</p>
              )}
              {data.sections.map((s) => (
                <label key={s.name}
                       className="flex items-center gap-3 border-b border-slate-100 px-3 py-2 last:border-0 hover:bg-slate-50">
                  <input type="checkbox" checked={picked.includes(s.name)}
                         onChange={() => toggle(s.name)}
                         data-testid={`win-section-${s.name}`} />
                  <span className="flex-1 text-sm text-slate-800">{s.name}
                    <span className="block text-[11px] text-slate-400">
                      {s.lines} line{s.lines === 1 ? "" : "s"} · {s.hours} labour hours
                    </span>
                  </span>
                  <span className="text-sm font-semibold tabular-nums text-slate-900">
                    {money(s.sell)}
                  </span>
                </label>
              ))}
              {data.acceleration.offered && (
                <label className="flex items-center gap-3 bg-amber-50/60 px-3 py-2"
                       data-testid="win-acceleration-row">
                  <input type="checkbox" checked={accel}
                         onChange={(e) => setAccel(e.target.checked)}
                         data-testid="win-acceleration" />
                  <span className="flex-1 text-sm text-slate-800">
                    <span className="flex items-center gap-1.5 font-medium">
                      <Zap className="h-3.5 w-3.5 text-amber-500" />
                      Schedule acceleration accepted
                    </span>
                    <span className="block text-[11px] text-slate-500">
                      {data.acceleration.ot_hours} overtime hours at{" "}
                      {money(data.acceleration.ot_premium_rate)}/h. Ticked here it is part of the
                      main contract; if they accept later it goes in as a change order.
                    </span>
                  </span>
                  <span className="text-sm font-semibold tabular-nums text-slate-900">
                    {money(data.acceleration.amount)}
                  </span>
                </label>
              )}
            </div>

            <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
              <span className="text-xs text-slate-500">
                {picked.length} of {data.sections.length} scopes
                {picked.length < data.sections.length
                  && ` · ${money((data.total_sell || 0) - wonSell)} not awarded`}
              </span>
              <span className="text-sm font-semibold text-slate-900" data-testid="win-contract-value">
                Contract {money(contract)}
              </span>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}
                  data-testid="win-cancel">Cancel</Button>
          <Button onClick={push} disabled={busy || !data || picked.length === 0}
                  data-testid="win-push">
            {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Push to project
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
